/**
 * Le journal des jets.
 *
 * Une table à part, et **jamais reprise** : un jet est un fait, pas un état.
 * C'est ce qui la rendra immunisée aux conflits de synchronisation — deux
 * appareils qui journalisent chacun de leur côté se fusionnent sans arbitrage,
 * là où deux versions d'une fiche demanderaient de trancher.
 *
 * La pile de modificateurs est conservée telle qu'elle a été appliquée. Le jour
 * où une valeur du catalogue change, l'historique continue de dire ce qui s'est
 * passé ce soir-là, et non ce qui se passerait aujourd'hui.
 */

import type { Table } from 'dexie'

import { base } from './base'
import type { Resolution } from '../rules'

export interface JetJournalise {
  id: string
  personnage_id: string
  /** L'identifiant de la Compétence testée. */
  competence: string
  /** Son nom imprimé, gardé avec le jet : le catalogue, lui, peut changer. */
  nom: string
  d100: number
  reelle: number
  difficulte: number
  verdict: Resolution['verdict']
  reussite: boolean
  critique: boolean
  degres: number
  /** Les deux dés d'un Jet chanceux, avant composition. Absent sinon. */
  des?: number[]
  /** Ce que le coup a infligé, quand c'était une Attaque armée. */
  degats?: number
  termes: { origine: string; valeur: number }[]
  /** ISO 8601, en UTC. */
  horodatage: string
}

const table = () => base.table('jets') as Table<JetJournalise, string>

/** Ajoute un jet au journal. Rien n'y est jamais repris. */
export async function journaliser(
  personnageId: string,
  competence: string,
  nom: string,
  resolution: Resolution,
  des?: number[],
): Promise<void> {
  await table().add({
    id: crypto.randomUUID(),
    personnage_id: personnageId,
    competence,
    nom,
    d100: resolution.d100,
    reelle: resolution.reelle,
    difficulte: resolution.difficulte,
    verdict: resolution.verdict,
    reussite: resolution.reussite,
    critique: resolution.critique,
    degres: resolution.degres,
    degats: resolution.degats?.total,
    des,
    termes: resolution.termes,
    horodatage: new Date().toISOString(),
  })
}

/** Les derniers jets d'un personnage, le plus récent en tête. */
export async function derniers(personnageId: string, combien = 8): Promise<JetJournalise[]> {
  const tout = await table().where('personnage_id').equals(personnageId).toArray()
  return tout.sort((a, b) => b.horodatage.localeCompare(a.horodatage)).slice(0, combien)
}
